import { atom, computed, type ReadableAtom } from 'nanostores';
import { chatStore } from './chat';
import { setCookie } from '~/lib/.client/cookie';
import { clientFetch } from '~/lib/.client/fetch';
import { settingsService } from '~/lib/services/settings-service';
import type { APITokenStatsResponse } from '~/routes/api.token-stats';
import type { OrganizationData, OrganizationSettings } from '~/types/organization';
import { type UserData } from '~/types/user';
import { SELECTED_ACCOUNT_USER } from '~/utils/constants';
import { effectiveTier, type EffectiveTier } from '~/utils/effectiveTier';
import { invariant } from '~/utils/invariant';
import { withResolvers } from '~/utils/promises';

const ACTIVE_ORGANIZATION_ID = 'activeOrganizationId';

export class UserStore {
  #user = atom<UserData | undefined>();
  #tier = atom<EffectiveTier | undefined>();
  #featureFlags = computed(this.#user, (user) => user?.featureFlags ?? {});

  #initialized = withResolvers<UserData | null | undefined>();

  rateLimitReached = atom(false);
  freeUserRateLimited = atom(false);
  loggedIn = atom(false);
  canSuggestSwitchAccount = atom<boolean>(false);

  #activeOrganization = computed(
    this.#user,
    (user) => user?.organizations?.find(({ id }) => id === user.activeOrganizationId) ?? null,
  );

  #showAccountSelector = computed(this.#activeOrganization, (activeOrganization) => {
    return (
      (activeOrganization?.organizationSettings?.showAccountSelector ?? true) || activeOrganization?.role === 'admin'
    );
  });

  // indicates if the user is waiting for a subscription to be canceled
  readonly isSubscriptionBeingCanceled = computed(
    this.#user,
    (user) =>
      user?.membership?.subscription?.status === 'canceled' &&
      user?.membership?.tier >= 1 &&
      (user?.membership?.subscription?.currentPeriodEnd ?? 0) >= Date.now(),
  );

  readonly isSubscriptionBeingDowngraded = computed(
    this.#user,
    (user) =>
      user?.membership?.subscription?.status === 'active' &&
      user?.membership?.tier >= 1 &&
      (user?.membership?.nextTier ?? 0) < user?.membership?.tier,
  );

  readonly selectableOrganizations = computed([this.#user, this.#activeOrganization], (user, activeOrganization) => {
    return user?.organizations?.filter((organization) => organization.slug !== activeOrganization?.slug) ?? [];
  });

  get user(): ReadableAtom<UserData | undefined> {
    return this.#user;
  }

  get featureFlags(): ReadableAtom<UserData['featureFlags']> {
    return this.#featureFlags;
  }

  get initialized() {
    return this.#initialized.promise;
  }

  init(user?: UserData | null, loggedIn: boolean = false) {
    if (user) {
      this.setUser(user);

      const organizationWithoutAccountSelector = user.organizations?.find(
        (org) => org.organizationSettings?.showAccountSelector === false,
      );

      if (organizationWithoutAccountSelector) {
        userStore.setActiveOrganization(organizationWithoutAccountSelector, false);
      }
    }

    this.loggedIn.set(loggedIn);

    this.#initialized.resolve(user);
  }

  getTierStore(): ReadableAtom<EffectiveTier | undefined> {
    return this.#tier;
  }

  setUser(user: UserData) {
    this.#user.set(user);

    this.#tier.set(effectiveTier(user));
  }

  isPaidUser = computed(this.#user, (user) => user && effectiveTier(user).isPaid);

  getUser() {
    return this.#user.get();
  }

  setCanSuggestSwitchAccount(organization: OrganizationData | null) {
    const activeOrganization = userStore.getActiveOrganization().get();

    // if the user can use tokens from the project org but is on a different account, suggest to switch the account
    if (organization?.billing && activeOrganization?.id !== organization.id) {
      this.canSuggestSwitchAccount.set(true);
    }
  }

  async setActiveOrganization(organization: OrganizationData | null, redirect: boolean = true) {
    const user = this.getUser();

    invariant(user);

    user.activeOrganizationId = organization?.id ?? null;

    this.#user.set(structuredClone(user));

    try {
      const tier = effectiveTier(user);

      this.#tier.set(tier);
    } catch {
      this.#tier.set(undefined);
    }

    setCookie(ACTIVE_ORGANIZATION_ID, organization ? String(organization.id) : SELECTED_ACCOUNT_USER);

    // send back to the homepage
    if (redirect && window.location.pathname !== '/') {
      window.location.pathname = '/';
    }

    const [newTokenStats] = await Promise.all([clientFetch('/api/token-stats'), settingsService.loadSettings()]);

    if (!newTokenStats.ok) {
      throw new Error('Failed to load token usage');
    }

    const { tokenStats, user: updatedUser } = await newTokenStats.json<APITokenStatsResponse>();

    updatedUser.activeOrganizationId = organization?.id ?? null;

    chatStore.setTokenStats(tokenStats);

    this.setUser(updatedUser);
  }

  getActiveOrganization() {
    return this.#activeOrganization;
  }

  setLogout() {
    this.#user.set(undefined);
    this.#tier.set(undefined);
    this.loggedIn.set(false);
  }

  getShowAccountSelector() {
    return this.#showAccountSelector;
  }

  patchOrganizationSettings(organization: OrganizationData, settings: OrganizationSettings) {
    const user = this.getUser();
    const newUser = structuredClone(user);

    if (!newUser?.organizations) {
      return;
    }

    newUser.organizations = newUser.organizations.map((org) => {
      if (org.id === organization.id) {
        const newOrg = structuredClone(org);
        newOrg.organizationSettings = Object.assign({}, newOrg.organizationSettings, settings);

        return newOrg;
      }

      return org;
    });

    this.setUser(newUser);
  }

  removeOrganizationInvite(token: string) {
    const user = this.getUser();
    const newUser = structuredClone(user);

    if (!newUser?.organizationInvites) {
      return;
    }

    const newInvites = [];

    for (const invite of newUser.organizationInvites) {
      if (invite.token === token) {
        continue;
      }

      newInvites.push(invite);
    }

    newUser.organizationInvites = newInvites;

    this.setUser(newUser);
  }
}

export const userStore = new UserStore();

if (import.meta.env.DEV) {
  Object.assign(globalThis, { userStore });
}
